import React from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useSiteData } from '../context/SiteDataContext';
import { Home, Truck, Sparkles, Square, Building2, ArrowLeft, CheckCircle, Phone, Calendar } from 'lucide-react';
import { Button } from '../components/ui/button';
import CTASection from '../components/CTASection';

const iconMap = {
  Home,
  Truck,
  Sparkles,
  Square,
  Building2,
  Stairs: Building2,
};

const ServiceDetailPage = () => {
  const { id } = useParams();  // This is now the slug or id
  const navigate = useNavigate();
  const { language, t } = useLanguage();
  const { services, companyInfo } = useSiteData();
  
  const serviceList = services[language];
  // Find by slug first, then by id for backward compatibility
  const service = serviceList.find(s => s.slug === id) || serviceList.find(s => s.id === id);
  
  // If service not found, show error state
  if (!service) {
    return (
      <main>
        <section className="py-20 bg-gray-50">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">
              {language === 'sv' ? 'Tjänsten hittades inte' : 'Service not found'}
            </h1>
            <p className="text-gray-600 mb-8">
              {language === 'sv' 
                ? 'Tyvärr kunde vi inte hitta den begärda tjänsten.'
                : 'Sorry, we could not find the requested service.'
              }
            </p>
            <Link to="/services">
              <Button className="bg-purple-600 hover:bg-purple-700">
                <ArrowLeft size={20} className="mr-2" />
                {language === 'sv' ? 'Tillbaka till tjänster' : 'Back to services'}
              </Button>
            </Link>
          </div>
        </section>
      </main>
    );
  }

  const Icon = iconMap[service.icon] || Sparkles;
  
  // Default features (fallback ONLY if service doesn't have features field at all - for backward compatibility)
  const defaultFeatures = language === 'sv' ? [
    'All utrustning och rengöringsmedel ingår',
    'Försäkrad och ID-kontrollerad personal',
    'Nöjd-kund-garanti',
    'RUT-avdrag (50% skattereduktion)',
    'Flexibla bokningsalternativ',
    'Miljövänliga produkter'
  ] : [
    'All equipment and cleaning supplies included',
    'Insured and ID-verified staff',
    'Satisfaction guarantee',
    'RUT deduction (50% tax reduction)',
    'Flexible booking options',
    'Eco-friendly products'
  ];
  
  // Use service features if the field exists (even if empty), otherwise use defaults for backward compatibility
  // This allows admins to delete all features if they want
  const hasCustomFeatures = service.features !== undefined && service.features !== null;
  const features = hasCustomFeatures
    ? service.features.map(f => language === 'sv' ? f.text_sv : f.text_en).filter(Boolean)
    : defaultFeatures;

  return (
    <main>
      {/* Hero Section */}
      <section className="relative py-16 md:py-24 bg-gradient-to-br from-purple-900 via-purple-800 to-purple-900 overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-20 right-20 w-72 h-72 bg-orange-400 rounded-full blur-3xl" />
          <div className="absolute bottom-20 left-20 w-96 h-96 bg-purple-400 rounded-full blur-3xl" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4">
          {/* Back Link */}
          <Link 
            to="/services" 
            className="inline-flex items-center gap-2 text-purple-200 hover:text-white mb-8 transition-colors"
          >
            <ArrowLeft size={20} />
            {language === 'sv' ? 'Tillbaka till tjänster' : 'Back to services'}
          </Link>
          
          <div className="flex flex-col md:flex-row items-start gap-6">
            {/* Icon */}
            <div className="w-20 h-20 bg-white/10 backdrop-blur-sm rounded-2xl flex items-center justify-center flex-shrink-0">
              <Icon className="text-orange-400" size={40} />
            </div>
            
            {/* Title & Description */}
            <div>
              <h1 className="text-3xl md:text-5xl font-bold text-white mb-4">
                {service.title}
              </h1>
              <p className="text-xl text-purple-100 max-w-2xl">
                {service.description}
              </p>
              
              {/* Price Badge */}
              <div className="mt-6 inline-flex items-center gap-3 bg-white/10 backdrop-blur-sm rounded-full px-6 py-3">
                <span className="text-purple-200">
                  {language === 'sv' ? 'Från' : 'From'}
                </span>
                <span className="text-2xl font-bold text-orange-400">
                  {service.basePrice} kr
                </span>
                <span className="text-purple-200">
                  {service.priceUnit}
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Main Content */}
            <div className="lg:col-span-2">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                {language === 'sv' ? 'Om tjänsten' : 'About this service'}
              </h2>
              
              {/* Full Description */}
              <div className="prose prose-lg max-w-none text-gray-600 mb-10">
                {service.fullDescription ? (
                  <div className="whitespace-pre-wrap leading-relaxed">
                    {service.fullDescription}
                  </div>
                ) : (
                  <p className="leading-relaxed">
                    {service.description}
                  </p>
                )}
              </div>
              
              {/* Features List - only show if there are features */}
              {features.length > 0 && (
                <>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">
                    {language === 'sv' ? 'Det här ingår' : 'What\'s included'}
                  </h3>
                  <div className="grid sm:grid-cols-2 gap-4 mb-10">
                    {features.map((feature, index) => (
                      <div key={index} className="flex items-start gap-3">
                        <div className="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                          <CheckCircle className="text-purple-600" size={14} />
                        </div>
                        <span className="text-gray-700">{feature}</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
            
            {/* Sidebar */}
            <div className="lg:col-span-1">
              {/* CTA Card */}
              <div className="bg-gradient-to-br from-purple-50 to-orange-50 rounded-2xl p-6 border border-purple-100 sticky top-24">
                <h3 className="text-xl font-bold text-gray-900 mb-4">
                  {language === 'sv' ? 'Boka nu' : 'Book now'}
                </h3>
                <p className="text-gray-600 mb-6">
                  {language === 'sv' 
                    ? 'Kontakta oss för att boka eller få en kostnadsfri offert.'
                    : 'Contact us to book or get a free quote.'
                  }
                </p>
                
                {/* Price Highlight */}
                <div className="bg-white rounded-xl p-4 mb-6 shadow-sm">
                  <div className="text-sm text-gray-500 mb-1">
                    {language === 'sv' ? 'Pris från' : 'Price from'}
                  </div>
                  <div className="flex items-baseline gap-2">
                    <span className="text-3xl font-bold text-purple-600">
                      {service.basePrice} kr
                    </span>
                    <span className="text-gray-500">{service.priceUnit}</span>
                  </div>
                  <div className="text-sm text-orange-600 mt-2">
                    {language === 'sv' 
                      ? '* 50% RUT-avdrag tillgängligt'
                      : '* 50% RUT deduction available'
                    }
                  </div>
                </div>
                
                {/* Action Buttons */}
                <div className="space-y-3">
                  <Link to="/#calculator" className="block">
                    <Button className="w-full bg-purple-600 hover:bg-purple-700 py-6">
                      <Calendar size={20} className="mr-2" />
                      {language === 'sv' ? 'Beräkna pris' : 'Calculate price'}
                    </Button>
                  </Link>
                  
                  <a href={`tel:${companyInfo.phone}`} className="block">
                    <Button variant="outline" className="w-full py-6 border-2 border-purple-200 hover:bg-purple-50">
                      <Phone size={20} className="mr-2" />
                      {companyInfo.phone}
                    </Button>
                  </a>
                  
                  <Link to="/contact" className="block">
                    <Button variant="outline" className="w-full py-6 border-2 border-gray-200 hover:bg-gray-50">
                      {language === 'sv' ? 'Skicka förfrågan' : 'Send inquiry'}
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <CTASection />
    </main>
  );
};

export default ServiceDetailPage;
